import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InternshipApi } from "src/handler/accountHandler";

interface Internship {
  internshipId: number;
  internshipName: string;
  internshipDescription: string;

  internship30Days: number;
  internship45Days: number;
  internship60Days: number;
  internship90Days: number;
  internship180Days: number;
  internship270Days: number;
  internship360Days: number;
}

interface Course {
  courseId: number;
  courseName: string;
  courseDescription?: string;
  coursePrice?: number;
  image?: string;
}

interface InternshipPurchaseProps {
  courses?: Course[];
  onBack?: () => void;
}

type DurationKey = "30" | "45" | "60" | "90" | "180" | "270" | "360";

const InternshipPurchase: React.FC<InternshipPurchaseProps> = ({
  courses = [],
  onBack,
}) => {
  const navigate = useNavigate();

  /* =========================================================
       STATE
    ========================================================= */

  const [step, setStep] = useState<1 | 2 | 3>(1);

  const [internships, setInternships] = useState<Internship[]>([]);

  const [selectedInternship, setSelectedInternship] =
    useState<Internship | null>(null);

  const [selectedDuration, setSelectedDuration] = useState<DurationKey | null>(
    null,
  );

  const [selectedCourses, setSelectedCourses] = useState<Course[]>([]);

  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  /*
   * Reference for the bottom navigation.
   *
   * When the 3rd course is selected, we scroll here.
   */
  const bottomNavigationRef = useRef<HTMLDivElement>(null);

  /* =========================================================
       LOAD INTERNSHIPS
    ========================================================= */

  useEffect(() => {
    loadInternships();
  }, []);

  const loadInternships = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await InternshipApi({
        internship: {
        },
        action: 1,
      });

      if (response?.success && response?.data?.internships) {
        setInternships(response.data.internships);
      } else {
        setError(response?.message || "No internships available.");
      }
    } catch (err) {
      console.error("Internship API Error:", err);

      setError("Unable to load internships. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
       DURATION OPTIONS
    ========================================================= */

  const durationOptions: {
    key: DurationKey;
    label: string;
    field: keyof Internship;
  }[] = [
    {
      key: "30",
      label: "30 Days",
      field: "internship30Days",
    },
    {
      key: "45",
      label: "45 Days",
      field: "internship45Days",
    },
    {
      key: "60",
      label: "60 Days",
      field: "internship60Days",
    },
    {
      key: "90",
      label: "90 Days",
      field: "internship90Days",
    },
    {
      key: "180",
      label: "180 Days",
      field: "internship180Days",
    },
    {
      key: "270",
      label: "270 Days",
      field: "internship270Days",
    },
    {
      key: "360",
      label: "360 Days",
      field: "internship360Days",
    },
  ];

  /* =========================================================
       GET SELECTED PRICE
    ========================================================= */

  const selectedPrice = useMemo(() => {
    if (!selectedInternship || !selectedDuration) {
      return 0;
    }

    const duration = durationOptions.find(
      (item) => item.key === selectedDuration,
    );

    if (!duration) {
      return 0;
    }

    return Number(selectedInternship[duration.field]) || 0;
  }, [selectedInternship, selectedDuration]);

  /* =========================================================
       FILTER COURSES
    ========================================================= */

  const filteredCourses = useMemo(() => {
    if (!search.trim()) {
      return courses;
    }

    return courses.filter((course) =>
      course.courseName?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [courses, search]);

  /* =========================================================
       COURSE SELECTION
       MAXIMUM = 3
    ========================================================= */

  const isCourseSelected = (courseId: number) => {
    return selectedCourses.some((course) => course.courseId === courseId);
  };

  const toggleCourse = (course: Course) => {
    const alreadySelected = isCourseSelected(course.courseId);

    /* =====================================================
           REMOVE COURSE
        ===================================================== */

    if (alreadySelected) {
      setSelectedCourses((previous) =>
        previous.filter((item) => item.courseId !== course.courseId),
      );

      return;
    }

    /* =====================================================
           DO NOT ALLOW 4TH COURSE
        ===================================================== */

    if (selectedCourses.length >= 3) {
      return;
    }

    const newSelectedCourses = [...selectedCourses, course];

    setSelectedCourses(newSelectedCourses);

    /* =====================================================
           AFTER SELECTING 3RD COURSE
           SCROLL TO PAYMENT BUTTON
        ===================================================== */

    if (newSelectedCourses.length === 3) {
      setTimeout(() => {
        bottomNavigationRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200);
    }
  };

  /* =========================================================
       STEP VALIDATION
    ========================================================= */

  const canGoToStep2 = selectedInternship !== null;

  const canGoToStep3 = selectedInternship !== null && selectedDuration !== null;

  const canPurchase =
    selectedInternship !== null &&
    selectedDuration !== null &&
    selectedCourses.length === 3;

  /* =========================================================
       SCROLL TO TOP
    ========================================================= */

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =========================================================
       NEXT
    ========================================================= */

  const goNext = () => {
    if (step === 1 && canGoToStep2) {
      setStep(2);

      setTimeout(() => {
        scrollToTop();
      }, 50);

      return;
    }

    if (step === 2 && canGoToStep3) {
      setStep(3);

      setTimeout(() => {
        scrollToTop();
      }, 50);

      return;
    }
  };

  /* =========================================================
       BACK
    ========================================================= */

  const goBack = () => {
    if (step === 1) {
      if (onBack) {
        onBack();
      } else {
        navigate(-1);
      }

      return;
    }

    if (step === 2) {
      setStep(1);

      setTimeout(() => {
        scrollToTop();
      }, 50);

      return;
    }

    if (step === 3) {
      setStep(2);

      setTimeout(() => {
        scrollToTop();
      }, 50);

      return;
    }
  };

  /* =========================================================
       PAYMENT
    ========================================================= */

  const handlePayment = () => {
    if (!canPurchase || !selectedInternship || !selectedDuration) {
      return;
    }

    const purchaseData = {
      internshipId: selectedInternship.internshipId,

      internshipName: selectedInternship.internshipName,

      internshipDescription: selectedInternship.internshipDescription,

      duration: Number(selectedDuration),

      internshipPrice: selectedPrice,

      courses: selectedCourses.map((course) => ({
        courseId: course.courseId,

        courseName: course.courseName,

        coursePrice: course.coursePrice || 0,
      })),

      courseIds: selectedCourses.map((course) => course.courseId),
    };

    console.log("INTERNSHIP PURCHASE:", purchaseData);

    navigate("/internship-payment", {
      state: purchaseData,
    });
  };

  /* =========================================================
       FORMAT PRICE
    ========================================================= */

  const formatPrice = (price: number) => {
    return `₹${Number(price || 0).toLocaleString("en-IN")}`;
  };

  /* =========================================================
       RENDER STEP INDICATOR
    ========================================================= */

  const renderStepIndicator = () => {
    return (
      <div style={styles.stepWrapper}>
        {/* STEP 1 */}

        <div
          style={{
            ...styles.stepCircle,

            ...(step >= 1 ? styles.stepActive : {}),
          }}
        >
          {step > 1 ? "✓" : "1"}
        </div>

        {/* LINE */}

        <div
          style={{
            ...styles.stepLine,

            ...(step >= 2 ? styles.stepLineActive : {}),
          }}
        />

        {/* STEP 2 */}

        <div
          style={{
            ...styles.stepCircle,

            ...(step >= 2 ? styles.stepActive : {}),
          }}
        >
          {step > 2 ? "✓" : "2"}
        </div>

        {/* LINE */}

        <div
          style={{
            ...styles.stepLine,

            ...(step >= 3 ? styles.stepLineActive : {}),
          }}
        />

        {/* STEP 3 */}

        <div
          style={{
            ...styles.stepCircle,

            ...(step >= 3 ? styles.stepActive : {}),
          }}
        >
          3
        </div>
      </div>
    );
  };

  /* =========================================================
       STEP 1
    ========================================================= */

  const renderInternshipStep = () => {
    return (
      <section style={styles.card}>
        <div style={styles.stepHeading}>
          <div>
            <div style={styles.stepLabel}>STEP 1</div>

            <h2 style={styles.heading}>Choose Your Internship</h2>

            <p style={styles.subHeading}>
              Select the internship program you want to pursue.
            </p>
          </div>

          {selectedInternship && (
            <div style={styles.selectionBadge}>✓ Selected</div>
          )}
        </div>

        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner} />

            <div>Loading internships...</div>
          </div>
        ) : error ? (
          <div style={styles.errorContainer}>
            <div>{error}</div>

            <button onClick={loadInternships} style={styles.retryButton}>
              Retry
            </button>
          </div>
        ) : (
          <div style={styles.internshipGrid}>
            {internships.map((internship) => {
              const isSelected =
                selectedInternship?.internshipId === internship.internshipId;

              return (
                <div
                  key={internship.internshipId}
                  onClick={() => setSelectedInternship(internship)}
                  style={{
                    ...styles.internshipCard,

                    ...(isSelected ? styles.internshipCardSelected : {}),
                  }}
                >
                  <div style={styles.cardTop}>
                    <div style={styles.internshipIcon}>
                      {internship.internshipName?.charAt(0).toUpperCase()}
                    </div>

                    <div
                      style={{
                        ...styles.radio,

                        ...(isSelected ? styles.radioSelected : {}),
                      }}
                    >
                      {isSelected && "✓"}
                    </div>
                  </div>

                  <h3 style={styles.internshipName}>
                    {internship.internshipName}
                  </h3>

                  <p style={styles.internshipDescription}>
                    {internship.internshipDescription}
                  </p>

                  <div style={styles.cardFooter}>
                    <span>Starting from</span>

                    <strong>{formatPrice(internship.internship30Days)}</strong>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  /* =========================================================
       STEP 2
    ========================================================= */

  const renderDurationStep = () => {
    if (!selectedInternship) {
      return null;
    }

    return (
      <section style={styles.card}>
        <div style={styles.stepHeading}>
          <div>
            <div style={styles.stepLabel}>STEP 2</div>

            <h2 style={styles.heading}>Choose Internship Duration</h2>

            <p style={styles.subHeading}>
              Select how long you want to continue your internship.
            </p>
          </div>

          <div style={styles.selectedInternshipBadge}>
            {selectedInternship.internshipName}
          </div>
        </div>

        <div style={styles.durationGrid}>
          {durationOptions.map((duration) => {
            const price = Number(selectedInternship[duration.field]) || 0;

            const isSelected = selectedDuration === duration.key;

            return (
              <div
                key={duration.key}
                onClick={() => setSelectedDuration(duration.key)}
                style={{
                  ...styles.durationCard,

                  ...(isSelected ? styles.durationCardSelected : {}),
                }}
              >
                <div style={styles.durationTop}>
                  <div style={styles.durationNumber}>{duration.key}</div>

                  <div style={styles.durationDaysText}>DAYS</div>

                  <div
                    style={{
                      ...styles.durationRadio,

                      ...(isSelected ? styles.durationRadioSelected : {}),
                    }}
                  >
                    {isSelected && "✓"}
                  </div>
                </div>

                <div style={styles.durationPrice}>{formatPrice(price)}</div>

                <div style={styles.durationSmallText}>Internship fee</div>
              </div>
            );
          })}
        </div>

        {selectedDuration && (
          <div style={styles.pricePreview}>
            <div>
              <div style={styles.previewLabel}>SELECTED PLAN</div>

              <div style={styles.previewTitle}>
                {selectedInternship.internshipName} • {selectedDuration} Days
              </div>
            </div>

            <div style={styles.previewPrice}>{formatPrice(selectedPrice)}</div>
          </div>
        )}
      </section>
    );
  };

  /* =========================================================
       STEP 3
    ========================================================= */

  const renderCourseStep = () => {
    return (
      <section style={styles.card}>
        <div style={styles.stepHeading}>
          <div>
            <div style={styles.stepLabel}>STEP 3</div>

            <h2 style={styles.heading}>Choose 3 Courses</h2>

            <p style={styles.subHeading}>
              Select exactly 3 courses to complete your internship package.
            </p>
          </div>

          <div
            style={{
              ...styles.courseCounter,

              ...(selectedCourses.length === 3
                ? styles.courseCounterComplete
                : {}),
            }}
          >
            {selectedCourses.length} / 3
          </div>
        </div>

        {/* SEARCH */}

        <div style={styles.searchBox}>
          <span style={styles.searchIcon}>🔍</span>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search courses..."
            style={styles.searchInput}
          />
        </div>

        {/* INFORMATION */}

        <div
          style={{
            ...styles.courseInfo,

            ...(selectedCourses.length === 3 ? styles.courseInfoComplete : {}),
          }}
        >
          {selectedCourses.length === 3 ? (
            <>
              <span>✓</span>

              <span>
                You have selected exactly 3 courses. You can remove a course if
                you want to select another.
              </span>
            </>
          ) : (
            <>
              <span>ⓘ</span>

              <span>
                Please select <strong>{3 - selectedCourses.length}</strong> more
                course
                {3 - selectedCourses.length !== 1 ? "s" : ""}.
              </span>
            </>
          )}
        </div>

        {/* COURSE GRID */}

        <div style={styles.courseGrid}>
          {filteredCourses.map((course) => {
            const isSelected = isCourseSelected(course.courseId);

            const isDisabled = !isSelected && selectedCourses.length >= 3;

            return (
              <div
                key={course.courseId}
                onClick={() => {
                  if (!isDisabled) {
                    toggleCourse(course);
                  }
                }}
                style={{
                  ...styles.courseCard,

                  ...(isSelected ? styles.courseCardSelected : {}),

                  ...(isDisabled ? styles.courseCardDisabled : {}),
                }}
              >
                <div style={styles.courseTop}>
                  <div style={styles.courseIcon}>
                    {course.courseName?.charAt(0).toUpperCase()}
                  </div>

                  <div
                    style={{
                      ...styles.courseCheckbox,

                      ...(isSelected ? styles.courseCheckboxSelected : {}),
                    }}
                  >
                    {isSelected && "✓"}
                  </div>
                </div>

                <h3 style={styles.courseName}>{course.courseName}</h3>

                {course.courseDescription && (
                  <p style={styles.courseDescription}>
                    {course.courseDescription}
                  </p>
                )}

                {course.coursePrice !== undefined && (
                  <div style={styles.coursePrice}>
                    {formatPrice(course.coursePrice)}
                  </div>
                )}

                {isDisabled && (
                  <div style={styles.courseDisabledText}>Maximum 3 courses</div>
                )}
              </div>
            );
          })}
        </div>

        {filteredCourses.length === 0 && (
          <div style={styles.noCourses}>No courses found.</div>
        )}
      </section>
    );
  };

  /* =========================================================
       SUMMARY
    ========================================================= */

  const renderSummary = () => {
    if (!selectedInternship) {
      return null;
    }

    return (
      <div
  style={{
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "16px",
    padding: "18px",
    marginBottom: "20px",
    borderRadius: "18px",
    border: "1px solid rgba(0, 234, 255, 0.15)",
    background:
      "linear-gradient(135deg, rgba(9, 23, 42, 0.98), rgba(5, 13, 27, 0.98))",
    boxShadow:
      "0 10px 35px rgba(0, 0, 0, 0.22)",
    boxSizing: "border-box",
  }}
>
  {/* INTERNSHIP */}
  <div
    style={{
      flex: "1 1 220px",
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: "9px",
        fontWeight: 800,
        letterSpacing: "1.2px",
        color: "#71839e",
        marginBottom: "6px",
      }}
    >
      INTERNSHIP
    </div>

    <div
      style={{
        color: "#eef8ff",
        fontSize: "14px",
        fontWeight: 700,
        lineHeight: 1.4,
        wordBreak: "break-word",
      }}
    >
      {selectedInternship.internshipName}
    </div>
  </div>

  {/* DURATION */}
  {selectedDuration && (
    <div
      style={{
        flex: "0 1 100px",
        minWidth: "90px",
      }}
    >
      <div
        style={{
          fontSize: "9px",
          fontWeight: 800,
          letterSpacing: "1.2px",
          color: "#71839e",
          marginBottom: "6px",
        }}
      >
        DURATION
      </div>

      <div
        style={{
          color: "#eef8ff",
          fontSize: "14px",
          fontWeight: 700,
        }}
      >
        {selectedDuration} Days
      </div>
    </div>
  )}

  {/* COURSES */}
  {step === 3 && (
    <div
      style={{
        flex: "0 1 80px",
        minWidth: "70px",
      }}
    >
      <div
        style={{
          fontSize: "9px",
          fontWeight: 800,
          letterSpacing: "1.2px",
          color: "#71839e",
          marginBottom: "6px",
        }}
      >
        COURSES
      </div>

      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "48px",
          height: "28px",
          padding: "0 10px",
          borderRadius: "8px",
          color: "#67f3ff",
          background: "rgba(0, 234, 255, 0.08)",
          border: "1px solid rgba(0, 234, 255, 0.18)",
          fontSize: "12px",
          fontWeight: 800,
          boxSizing: "border-box",
        }}
      >
        {selectedCourses.length} / 3
      </div>
    </div>
  )}

  {/* PRICE */}
  {selectedDuration && (
    <div
      style={{
        flex: "0 1 150px",
        minWidth: "130px",
        paddingLeft: "20px",
        borderLeft: "1px solid rgba(255,255,255,0.08)",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          fontSize: "9px",
          fontWeight: 800,
          letterSpacing: "1.1px",
          color: "#71839e",
          marginBottom: "5px",
        }}
      >
        TOTAL INTERNSHIP FEE
      </div>

      <div
        style={{
          color: "#5ff5d0",
          fontSize: "20px",
          fontWeight: 900,
          letterSpacing: "-0.4px",
          whiteSpace: "nowrap",
        }}
      >
        {formatPrice(selectedPrice)}
      </div>
    </div>
  )}
</div>
    );
  };

  /* =========================================================
       MAIN
    ========================================================= */

  return (
    <div style={styles.page}>
      {/* HEADER */}

      <header style={styles.header}>
        <div style={styles.headerInner}>
          <button onClick={goBack} style={styles.backButton}>
            ← Back
          </button>

          <div style={styles.headerTitleBox}>
            <div style={styles.headerTitle}>Internship Purchase</div>

            <div style={styles.headerSubtitle}>
              Build your internship program
            </div>
          </div>

          {renderStepIndicator()}
        </div>
      </header>

      {/* CONTENT */}

      <main style={styles.container}>
        {/* HERO */}

        <div style={styles.hero}>
          <div>
            <div style={styles.heroBadge}>INTERNSHIP PROGRAM</div>

            <h1 style={styles.heroTitle}>Start your professional journey</h1>

            <p style={styles.heroDescription}>
              Choose an internship, select your duration, and build your
              learning experience with 3 courses.
            </p>
          </div>

          <div style={styles.heroRight}>
            <div style={styles.heroFeature}>
              <span>✓</span>
              Practical Learning
            </div>

            <div style={styles.heroFeature}>
              <span>✓</span>
              Flexible Duration
            </div>

            <div style={styles.heroFeature}>
              <span>✓</span>3 Courses Included
            </div>
          </div>
        </div>

        {/* STEP CONTENT */}

        {step === 1 && renderInternshipStep()}

        {step === 2 && renderDurationStep()}

        {step === 3 && renderCourseStep()}

        {/* SUMMARY */}

        {renderSummary()}

        {/* =================================================
                    BOTTOM NAVIGATION

                    REF IS HERE
                ================================================= */}

        <div ref={bottomNavigationRef} style={styles.bottomNavigation}>
          {/* BACK / PREVIOUS */}

          <button onClick={goBack} style={styles.secondaryButton}>
            ← {step === 1 ? "Back" : "Previous"}
          </button>

          {/* STEP 1 */}

          {step === 1 && (
            <button
              onClick={goNext}
              disabled={!canGoToStep2}
              style={{
                ...styles.primaryButton,

                ...(canGoToStep2
                  ? styles.primaryButtonEnabled
                  : styles.primaryButtonDisabled),
              }}
            >
              Choose Duration →
            </button>
          )}

          {/* STEP 2 */}

          {step === 2 && (
            <button
              onClick={goNext}
              disabled={!canGoToStep3}
              style={{
                ...styles.primaryButton,

                ...(canGoToStep3
                  ? styles.primaryButtonEnabled
                  : styles.primaryButtonDisabled),
              }}
            >
              Choose Courses →
            </button>
          )}

          {/* STEP 3 */}

          {step === 3 && (
            <button
              onClick={handlePayment}
              disabled={!canPurchase}
              style={{
                ...styles.primaryButton,

                ...(canPurchase
                  ? styles.paymentButton
                  : styles.primaryButtonDisabled),
              }}
            >
              {canPurchase
                ? "Proceed to Payment →"
                : `Select ${3 - selectedCourses.length} More Course${
                    3 - selectedCourses.length !== 1 ? "s" : ""
                  }`}
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

/* =============================================================
   STYLES
============================================================= */

const styles: {
  [key: string]: React.CSSProperties;
} = {
  /* =========================================================
       PAGE
    ========================================================= */

  page: {
    minHeight: "100vh",
    background: "#f5f7fa",
    color: "#172033",
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  /* =========================================================
       HEADER
    ========================================================= */

  header: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    background: "#ffffff",
    borderBottom: "1px solid #e5e9ee",
  },

  headerInner: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  backButton: {
    border: "1px solid #dce2e8",
    background: "#ffffff",
    color: "#475467",
    padding: "9px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
  },

  headerTitleBox: {
    minWidth: "190px",
  },

  headerTitle: {
    fontSize: "17px",
    fontWeight: 750,
    color: "#172033",
  },

  headerSubtitle: {
    marginTop: "2px",
    fontSize: "11px",
    color: "#8a94a3",
  },

  stepWrapper: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
  },

  stepCircle: {
    width: "29px",
    height: "29px",
    borderRadius: "50%",
    background: "#edf0f3",
    color: "#8993a0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    fontWeight: 750,
  },

  stepActive: {
    background: "#315fc4",
    color: "#ffffff",
  },

  stepLine: {
    width: "38px",
    height: "1px",
    background: "#dce1e6",
  },

  stepLineActive: {
    background: "#315fc4",
  },

  /* =========================================================
       CONTAINER
    ========================================================= */

  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "35px 24px 80px",
  },

  /* =========================================================
       HERO
    ========================================================= */

  hero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "35px",
    marginBottom: "28px",
    padding: "28px 30px",
    borderRadius: "16px",
    background: "#ffffff",
    border: "1px solid #e5e9ee",
    boxShadow: "0 3px 15px rgba(20,30,50,0.035)",
  },

  heroBadge: {
    display: "inline-block",
    padding: "5px 9px",
    borderRadius: "5px",
    background: "#edf3ff",
    color: "#315fc4",
    fontSize: "9px",
    fontWeight: 800,
    letterSpacing: "0.8px",
    marginBottom: "9px",
  },

  heroTitle: {
    margin: 0,
    fontSize: "29px",
    lineHeight: 1.2,
    fontWeight: 750,
    color: "#172033",
  },

  heroDescription: {
    maxWidth: "620px",
    margin: "9px 0 0",
    color: "#667085",
    fontSize: "14px",
    lineHeight: 1.65,
  },

  heroRight: {
    display: "flex",
    flexDirection: "column",
    gap: "9px",
    minWidth: "200px",
  },

  heroFeature: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    color: "#475467",
    fontSize: "12px",
    fontWeight: 600,
  },

  /* =========================================================
       CARD
    ========================================================= */

  card: {
    background: "#ffffff",
    border: "1px solid #e5e9ee",
    borderRadius: "15px",
    padding: "26px",
    boxShadow: "0 3px 15px rgba(20,30,50,0.035)",
  },

  stepHeading: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "20px",
    marginBottom: "25px",
  },

  stepLabel: {
    fontSize: "9px",
    letterSpacing: "1px",
    color: "#315fc4",
    fontWeight: 800,
    marginBottom: "5px",
  },

  heading: {
    margin: 0,
    fontSize: "22px",
    fontWeight: 750,
    color: "#172033",
  },

  subHeading: {
    margin: "6px 0 0",
    fontSize: "13px",
    color: "#7b8794",
    lineHeight: 1.5,
  },

  selectionBadge: {
    padding: "8px 12px",
    borderRadius: "7px",
    background: "#eaf8f1",
    color: "#138a55",
    fontSize: "11px",
    fontWeight: 700,
  },

  selectedInternshipBadge: {
    padding: "8px 12px",
    borderRadius: "7px",
    background: "#edf3ff",
    color: "#315fc4",
    fontSize: "11px",
    fontWeight: 700,
  },

  /* =========================================================
       INTERNSHIP
    ========================================================= */

  internshipGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "14px",
  },

  internshipCard: {
    border: "1px solid #e0e5eb",
    borderRadius: "12px",
    padding: "18px",
    background: "#ffffff",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  internshipCardSelected: {
    border: "2px solid #315fc4",
    background: "#f7faff",
    boxShadow: "0 5px 18px rgba(49,95,196,0.09)",
  },

  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  internshipIcon: {
    width: "43px",
    height: "43px",
    borderRadius: "10px",
    background: "#edf3ff",
    color: "#315fc4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "17px",
    fontWeight: 800,
  },

  radio: {
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    border: "1px solid #ccd4dd",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: 800,
  },

  radioSelected: {
    background: "#315fc4",
    borderColor: "#315fc4",
  },

  internshipName: {
    margin: "15px 0 6px",
    fontSize: "16px",
    fontWeight: 700,
    color: "#172033",
  },

  internshipDescription: {
    margin: 0,
    minHeight: "40px",
    color: "#667085",
    fontSize: "12px",
    lineHeight: 1.6,
    whiteSpace: "pre-line",
  },

  cardFooter: {
    marginTop: "15px",
    paddingTop: "12px",
    borderTop: "1px solid #edf0f3",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#8a94a3",
    fontSize: "11px",
  },

  /* =========================================================
       DURATION
    ========================================================= */

  durationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: "12px",
  },

  durationCard: {
    border: "1px solid #dfe5eb",
    borderRadius: "11px",
    padding: "17px",
    cursor: "pointer",
    background: "#ffffff",
  },

  durationCardSelected: {
    border: "2px solid #315fc4",
    background: "#f6f9ff",
    boxShadow: "0 5px 15px rgba(49,95,196,0.08)",
  },

  durationTop: {
    position: "relative",
  },

  durationNumber: {
    fontSize: "25px",
    lineHeight: 1,
    fontWeight: 800,
    color: "#172033",
  },

  durationDaysText: {
    marginTop: "3px",
    fontSize: "9px",
    letterSpacing: "0.8px",
    color: "#8a94a3",
    fontWeight: 700,
  },

  durationRadio: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    border: "1px solid #ccd4dd",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    color: "#ffffff",
  },

  durationRadioSelected: {
    background: "#315fc4",
    borderColor: "#315fc4",
  },

  durationPrice: {
    marginTop: "17px",
    fontSize: "17px",
    fontWeight: 800,
    color: "#315fc4",
  },

  durationSmallText: {
    marginTop: "3px",
    fontSize: "10px",
    color: "#8a94a3",
  },

  pricePreview: {
    marginTop: "22px",
    padding: "17px 20px",
    borderRadius: "10px",
    background: "#f7f9fb",
    border: "1px solid #e7ebef",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  previewLabel: {
    fontSize: "9px",
    letterSpacing: "0.8px",
    fontWeight: 800,
    color: "#8a94a3",
  },

  previewTitle: {
    marginTop: "4px",
    fontSize: "14px",
    fontWeight: 700,
    color: "#172033",
  },

  previewPrice: {
    fontSize: "23px",
    fontWeight: 800,
    color: "#315fc4",
  },

  /* =========================================================
       COURSES
    ========================================================= */

  courseCounter: {
    padding: "8px 14px",
    borderRadius: "8px",
    background: "#f1f3f6",
    color: "#667085",
    fontSize: "13px",
    fontWeight: 800,
  },

  courseCounterComplete: {
    background: "#e9f8f1",
    color: "#138a55",
  },

  searchBox: {
    height: "43px",
    border: "1px solid #dce2e8",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    padding: "0 13px",
    marginBottom: "15px",
    background: "#ffffff",
  },

  searchIcon: {
    marginRight: "8px",
    fontSize: "13px",
  },

  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: "13px",
    color: "#172033",
  },

  courseInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "11px 13px",
    marginBottom: "17px",
    borderRadius: "8px",
    background: "#f8f9fb",
    border: "1px solid #e7ebef",
    color: "#667085",
    fontSize: "12px",
  },

  courseInfoComplete: {
    background: "#effaf5",
    borderColor: "#cdeee0",
    color: "#137a4d",
  },

  courseGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    gap: "13px",
  },

  courseCard: {
    border: "1px solid #e0e5eb",
    borderRadius: "11px",
    padding: "16px",
    cursor: "pointer",
    background: "#ffffff",
  },

  courseCardSelected: {
    border: "2px solid #315fc4",
    background: "#f7faff",
  },

  courseCardDisabled: {
    opacity: 0.43,
    cursor: "not-allowed",
    background: "#f7f8fa",
  },

  courseTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  courseIcon: {
    width: "37px",
    height: "37px",
    borderRadius: "9px",
    background: "#f0f2f5",
    color: "#475467",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: 800,
  },

  courseCheckbox: {
    width: "21px",
    height: "21px",
    borderRadius: "5px",
    border: "1px solid #cbd3dc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    color: "#ffffff",
    fontWeight: 800,
  },

  courseCheckboxSelected: {
    background: "#315fc4",
    borderColor: "#315fc4",
  },

  courseName: {
    margin: "13px 0 5px",
    fontSize: "14px",
    fontWeight: 700,
    color: "#172033",
  },

  courseDescription: {
    margin: 0,
    minHeight: "34px",
    fontSize: "11px",
    color: "#7b8794",
    lineHeight: 1.5,
  },

  coursePrice: {
    marginTop: "10px",
    fontSize: "12px",
    color: "#315fc4",
    fontWeight: 750,
  },

  courseDisabledText: {
    marginTop: "8px",
    fontSize: "9px",
    color: "#a0a8b3",
    fontWeight: 600,
  },

  noCourses: {
    padding: "35px",
    textAlign: "center",
    color: "#8a94a3",
    fontSize: "13px",
  },

  /* =========================================================
       LOADING
    ========================================================= */

  loadingContainer: {
    minHeight: "230px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    color: "#7b8794",
    fontSize: "13px",
  },

  spinner: {
    width: "30px",
    height: "30px",
    border: "3px solid #e7ebef",
    borderTop: "3px solid #315fc4",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  errorContainer: {
    padding: "20px",
    borderRadius: "9px",
    background: "#fff5f5",
    border: "1px solid #ffd7d7",
    color: "#b42318",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "13px",
  },

  retryButton: {
    border: "none",
    background: "#b42318",
    color: "#ffffff",
    padding: "8px 13px",
    borderRadius: "7px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 700,
  },

  /* =========================================================
       SUMMARY
    ========================================================= */

  summaryCard: {
    marginTop: "20px",
    padding: "17px 20px",
    borderRadius: "12px",
    background: "#172033",
    display: "flex",
    alignItems: "center",
    gap: "30px",
    color: "#ffffff",
  },

  summaryItem: {
    minWidth: "120px",
  },

  summaryLabel: {
    fontSize: "8px",
    letterSpacing: "0.8px",
    color: "#aab3c0",
    fontWeight: 800,
    marginBottom: "4px",
  },

  summaryValue: {
    fontSize: "13px",
    color: "#ffffff",
    fontWeight: 700,
  },

  summaryPriceContainer: {
    marginLeft: "auto",
    textAlign: "right",
  },

  summaryPrice: {
    fontSize: "20px",
    color: "#ffffff",
    fontWeight: 800,
  },

  /* =========================================================
       NAVIGATION
    ========================================================= */

  bottomNavigation: {
    marginTop: "22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "15px",

    /*
     * Extra bottom space makes sure that when the
     * payment button is scrolled into view, it has
     * comfortable space around it.
     */
    paddingBottom: "20px",
  },

  secondaryButton: {
    border: "1px solid #d8dee6",
    background: "#ffffff",
    color: "#475467",
    borderRadius: "8px",
    padding: "12px 19px",
    fontSize: "13px",
    fontWeight: 650,
    cursor: "pointer",
  },

  primaryButton: {
    minWidth: "190px",
    border: "none",
    borderRadius: "8px",
    padding: "13px 20px",
    fontSize: "13px",
    fontWeight: 750,
  },

  primaryButtonEnabled: {
    background: "#315fc4",
    color: "#ffffff",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(49,95,196,0.18)",
  },

  primaryButtonDisabled: {
    background: "#dfe4e9",
    color: "#9aa3ad",
    cursor: "not-allowed",
  },

  paymentButton: {
    background: "#172033",
    color: "#ffffff",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(23,32,51,0.18)",
  },
};

export default InternshipPurchase;
